import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  AlertTriangle, ArrowLeft, Archive, CheckCircle2, Circle, Link2, Loader2,
  MessageSquare, Phone, Mail, Plus, Settings, WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SectionHelp from "@/components/help/SectionHelp";

const CHANNEL_ICONS = { whatsapp: MessageSquare, email: Mail, voice: Phone, sms: MessageSquare, ai: Settings, social: Link2, other: Settings };
const CHANNEL_LABELS = { whatsapp: "WhatsApp", email: "Email", voice: "Voz", sms: "SMS", ai: "Inteligencia artificial", social: "Redes / páginas", other: "Otros" };
const DIRECTION_LABELS = { inbound: "Entrada", outbound: "Salida", bidirectional: "Entrada y salida" };

const MODULE_LABELS = {
  inbound_lead_whatsapp: ["Inbox", "IA WhatsApp", "Captación de leads"],
  outbound_campaign_whatsapp: ["Marketing", "Campañas WhatsApp"],
  internal_team_notify_whatsapp: ["Triggers", "Avisos al equipo"],
  inbound_lead_voice: ["Voz entrante", "IA en llamadas"],
  outbound_manual_voice: ["Llamadas salientes"],
  outbound_campaign_voice: ["Campañas de voz"],
};

const WA_PROVIDERS = [
  { key: "meta_whatsapp", label: "Meta WhatsApp Cloud API" },
  { key: "gupshup", label: "Gupshup" },
  { key: "twilio_whatsapp", label: "Twilio WhatsApp" },
  { key: "evolution_api", label: "Evolution API (managed)" },
];

const STATUS_MAP = {
  connected: { color: "bg-emerald-50 text-emerald-600 border-emerald-200", label: "Conectado" },
  pending_setup: { color: "bg-amber-50 text-amber-600 border-amber-200", label: "Pendiente" },
  pending_qr: { color: "bg-amber-50 text-amber-600 border-amber-200", label: "Esperando QR" },
  disconnected: { color: "bg-red-50 text-red-500 border-red-200", label: "Desconectado" },
  error: { color: "bg-red-50 text-red-500 border-red-200", label: "Error" },
};

export default function IntegrationPurposes() {
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});
  const [showNewInstance, setShowNewInstance] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [newInstKey, setNewInstKey] = useState("");
  const [newInstName, setNewInstName] = useState("");
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/tenant/channel-capabilities-matrix");
      setMatrix(res.data);
      setError(null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setError(e?.response?.data?.detail?.includes("no habilitada") || e?.response?.data?.detail?.includes("not enabled") ? "plan" : "role");
      } else {
        toast.error(formatApiError(e?.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCapability = async (instanceId, capKey, enabled, needsAck = false) => {
    const sKey = `${instanceId}-${capKey}`;
    setSaving(p => ({ ...p, [sKey]: true }));
    try {
      await api.put(`/tenant/integration-instances/${instanceId}/capabilities`, {
        capabilities: [{ capability_key: capKey, enabled, explicit_risk_ack: needsAck }],
      });
      toast.success(enabled ? "Capacidad habilitada" : "Capacidad deshabilitada");
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm("Esta combinación mezcla atención al cliente con avisos internos. ¿Confirmas?\n\nRecomendamos usar números separados.")) {
          await toggleCapability(instanceId, capKey, enabled, true);
          return;
        }
      } else {
        toast.error(formatApiError(e?.response?.data?.detail));
      }
    } finally {
      setSaving(p => ({ ...p, [sKey]: false }));
    }
  };

  const createInstance = async () => {
    if (!selectedProvider || !newInstKey.trim() || !newInstName.trim()) {
      toast.error("Completa todos los campos");
      return;
    }
    setCreating(true);
    try {
      await api.post("/tenant/integration-instances", {
        provider_key: selectedProvider,
        instance_key: newInstKey.trim().toLowerCase().replace(/\s+/g, "_"),
        instance_name: newInstName.trim(),
        channel_type: "whatsapp",
      });
      toast.success("Instancia creada");
      setShowNewInstance(false);
      setSelectedProvider("");
      setNewInstKey("");
      setNewInstName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const archiveInstance = async (instanceId, instanceName) => {
    if (!window.confirm(`¿Archivar "${instanceName}"? Ya no aparecerá aquí. No se puede archivar si sigue asignada a una capacidad activa.`)) return;
    setArchiving(p => ({ ...p, [instanceId]: true }));
    try {
      await api.delete(`/tenant/integration-instances/${instanceId}`);
      toast.success("Instancia archivada");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setArchiving(p => ({ ...p, [instanceId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="purposes-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#94A3B8]" />
      </div>
    );
  }

  if (error === "role") {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid="purposes-error-role">
        <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-[#0F172A]">Acceso restringido</h2>
        <p className="text-sm text-[#64748B] mt-2">Solo el dueño o administrador puede configurar capacidades de canales.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/tenant/integrations")}>Volver</Button>
      </div>
    );
  }

  if (error === "plan") {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid="purposes-error-plan">
        <Settings className="h-10 w-10 text-[#94A3B8] mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-[#0F172A]">Tu plan no incluye esta función</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/tenant/integrations")}>Volver</Button>
      </div>
    );
  }

  const groups = {};
  matrix.forEach(inst => {
    if (!groups[inst.channel_type]) groups[inst.channel_type] = [];
    groups[inst.channel_type].push(inst);
  });

  return (
    <div className="space-y-6 max-w-4xl" data-testid="integration-purposes-page">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/tenant/integrations")} data-testid="back-to-integrations" className="text-[#64748B]">
          <ArrowLeft className="h-4 w-4 mr-1" /> Integraciones
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate("/tenant/integrations?modal=evolution")}>
          Conectar Evolution
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Capacidades de canales</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Asigna qué hace cada número o línea: Inbox, campañas, avisos al equipo, voz, etc.
        </p>
      </div>

      <SectionHelp
        module="channel_capabilities"
        title="¿Para qué sirve cada capacidad?"
        description="Cada integración puede tener varias funciones. Por ejemplo: un WhatsApp Evolution para que la IA responda leads (entrada), y otro Twilio solo para avisar a vendedores (salida interna). Sin la capacidad correcta, los webhooks se ignoran aunque el proveedor esté conectado."
        whatItDoesNot="No reemplaza conectar el proveedor en Integraciones. Primero conecta, luego asigna capacidades aquí."
      />

      {matrix.length === 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center" data-testid="empty-no-instances">
          <Link2 className="h-10 w-10 text-[#CBD5E1] mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-[#0F172A]">Primero conecta un canal en Integraciones</h3>
          <Button size="sm" className="mt-4 bg-[#0F172A] text-white" onClick={() => navigate("/tenant/integrations")}>Ir a Integraciones</Button>
        </div>
      )}

      {Object.entries(groups).map(([channelType, instances]) => {
        const Icon = CHANNEL_ICONS[channelType] || Settings;
        const label = CHANNEL_LABELS[channelType] || channelType;
        return (
          <div key={channelType} className="space-y-3" data-testid={`channel-group-${channelType}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-[#475569]" />
                <h2 className="text-base font-semibold text-[#0F172A]">{label}</h2>
                <span className="text-xs text-[#94A3B8]">{instances.length} instancia{instances.length !== 1 ? "s" : ""}</span>
              </div>
              {channelType === "whatsapp" && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowNewInstance(true)} data-testid="add-whatsapp-instance">
                  <Plus className="h-3 w-3 mr-1" /> Agregar instancia
                </Button>
              )}
            </div>

            {instances.map(inst => {
              const st = STATUS_MAP[inst.connection_status] || STATUS_MAP.pending_setup;
              const enabledCaps = new Set((inst.capabilities || []).filter(c => c.is_enabled).map(c => c.capability_key));
              return (
                <div key={inst.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`instance-card-${inst.instance_key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs border ${st.color}`}>
                      {inst.connection_status === "connected" ? <CheckCircle2 className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                      <span className="font-medium">{inst.instance_name}</span>
                      <span className="opacity-70">{st.label}</span>
                      <span className="opacity-50">· {inst.provider_key}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#94A3B8] hover:text-red-500"
                      disabled={archiving[inst.id]} onClick={() => archiveInstance(inst.id, inst.instance_name)}
                      data-testid={`archive-instance-${inst.instance_key}`} title="Archivar instancia">
                      {archiving[inst.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                    </Button>
                  </div>

                  {/* El verde de arriba solo dice que se guardaron credenciales.
                      Esto dice si de verdad ha entrado un mensaje — que es lo que
                      el dueño cree que significa el verde. Un número puede estar
                      "Conectado" y mudo porque falta pegar esta URL en la consola
                      del proveedor, y así estuvo uno sin que nada lo avisara. */}
                  {inst.recepcion && !inst.recepcion.ok && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`recepcion-pendiente-${inst.instance_key}`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-amber-900">{inst.recepcion.titulo}</p>
                          <p className="text-xs text-amber-800 mt-0.5">{inst.recepcion.detalle}</p>
                          {inst.recepcion.ruta_webhook && (
                            <div className="mt-2 flex items-center gap-2">
                              <code className="flex-1 truncate rounded bg-white/70 px-2 py-1 text-[11px] text-amber-900 border border-amber-200">
                                {window.location.origin + inst.recepcion.ruta_webhook}
                              </code>
                              <Button variant="outline" size="sm" className="h-7 text-xs shrink-0"
                                onClick={() => {
                                  navigator.clipboard.writeText(window.location.origin + inst.recepcion.ruta_webhook);
                                  toast.success('URL copiada. Pégala en el panel de tu proveedor.');
                                }}
                                data-testid={`copiar-webhook-${inst.instance_key}`}>
                                Copiar URL
                              </Button>
                              {inst.provider_key === 'gupshup' && (
                                <Button variant="outline" size="sm" className="h-7 text-xs shrink-0"
                                  onClick={async () => {
                                    try {
                                      const r = await api.post('/integrations/gupshup/alta-asistida');
                                      toast.success(r.data.aviso);
                                      if (r.data.liga_meta) window.open(r.data.liga_meta, '_blank');
                                    } catch (e) {
                                      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo registrar en Gupshup');
                                    }
                                  }}>
                                  Registrar automáticamente
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {inst.recepcion && inst.recepcion.ok && inst.recepcion.estado === 'sin_trafico_reciente' && (
                    <p className="mb-3 text-xs text-[#94A3B8]" data-testid={`recepcion-callada-${inst.instance_key}`}>
                      {inst.recepcion.titulo}. {inst.recepcion.detalle}
                    </p>
                  )}

                  <div className="space-y-2">
                    {(inst.available_capabilities || []).map(avail => {
                      const isEnabled = enabledCaps.has(avail.capability_key);
                      const sKey = `${inst.id}-${avail.capability_key}`;
                      const modules = MODULE_LABELS[avail.capability_key] || [];
                      return (
                        <label key={avail.capability_key} className="flex items-start gap-3 p-3 rounded-lg border border-transparent hover:border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer" data-testid={`cap-toggle-${inst.instance_key}-${avail.capability_key}`}>
                          <Checkbox
                            checked={isEnabled}
                            disabled={saving[sKey]}
                            onCheckedChange={(checked) => toggleCapability(inst.id, avail.capability_key, !!checked)}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-[#0F172A]">{avail.ui_label}</span>
                              <Badge variant="outline" className="text-[10px] text-[#94A3B8]">
                                {DIRECTION_LABELS[avail.direction] || avail.direction}
                              </Badge>
                              {saving[sKey] && <Loader2 className="h-3 w-3 animate-spin text-[#94A3B8]" />}
                            </div>
                            <p className="text-xs text-[#64748B] mt-0.5">{avail.help_text}</p>
                            {modules.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {modules.map(m => (
                                  <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100">{m}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      <Dialog open={showNewInstance} onOpenChange={setShowNewInstance}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar instancia WhatsApp</DialogTitle>
            <DialogDescription>Para un segundo número o proveedor distinto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              {WA_PROVIDERS.map(p => (
                <button key={p.key} type="button"
                  className={`text-left p-2 rounded-lg border text-xs ${selectedProvider === p.key ? "border-[#0F172A] bg-[#F8F9FA]" : "border-[#E2E8F0]"}`}
                  onClick={() => setSelectedProvider(p.key)}>
                  {p.label}
                </button>
              ))}
            </div>
            <Input placeholder="Clave única (ej: equipo_interno)" value={newInstKey} onChange={e => setNewInstKey(e.target.value)} />
            <Input placeholder="Nombre visible" value={newInstName} onChange={e => setNewInstName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowNewInstance(false)}>Cancelar</Button>
            <Button size="sm" className="bg-[#0F172A] text-white" onClick={createInstance} disabled={creating || !selectedProvider}>
              {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />} Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}